/**
 * Copyright Motili Inc., 2016 All Rights Reserved
 */

import * as ApiService from './ApiService';

import { mapSmartyStreetsTimezone } from './TimezoneService';

/**
 * motili address autosuggest api
 * @param {string} value
 * @returns {Promise<Array>} response
 */
export function suggest(value, country = 'US') {
    return ApiService.get(true, 'Utilities/addresses/suggestions', {
        prefix: value,
        minPrefexLength: undefined,
        limit: undefined,
        country,
    });
}

/**
 * motili address autosuggest api, returns first result
 *
 * @param {string} value
 * @param {boolean} [existingOnly]
 * @returns {Promise<Object>} response
 */
export async function suggestOne(value) {
    const suggestions = await suggest(value);
    if (suggestions.length === 0) {
        return null;
    }
    return suggestions[0];
}

/**
 * motili address validation api
 * @param {string} address
 * @returns {Promise<Object>} response
 */
export function standardize(address, timezones, country = 'US') {
    return ApiService.get(true, 'Utilities/addresses/standardize', {
        address,
        country,
    }).then(response => ({
        ...response.address,
        timezone: mapSmartyStreetsTimezone(response.address.timezone, timezones)
            .id,
    }));
}

/**
 * motili address validation api
 * @param {object} address
 * @returns {Promise<Object>} response
 */
export function validateAdvancedAddress(address) {
    return ApiService.post(
        true,
        'Utilities/addresses/validateAdvancedAddress',
        {
            address,
        }
    ).then(response => response);
}

/**
 * motili zip code validation api
 * @param {string} zipCode
 * @param {string} country
 */
export function zipCodeStandardize(zipCode, country) {
    return ApiService.get(true, 'Utilities/addresses/zipCodeStandardize', {
        zipCode,
        country,
    });
}

/**
 * parses an address object and returns a string
 * @param {id?: number, rawAddress?: string, stdAddress?: string, stdDeliveryLine?: string, stdDeliveryLine2?: string, stdCity: string, stdRegion: string, stdPostalCode?: string, stdPostalCodePlus4?: string, stdCountry: string, standardized?: boolean, latitude?: number, longitude?: number, timezone?: string} address
 * @param {String} address.standardized
 * @param {String} address.rawAddress
 * @param {String} [address.stdAddress]
 * @param {String} [address.stdDeliveryLine]
 * @param {String} [address.stdCity]
 * @param {String} [address.stdRegion]
 * @param {String} [address.stdPostalCode]
 * @returns {String}
 */
export function parseFullAddressStringFromAddressObject(
    address,
    showCountry = false
) {
    if (!address) {
        return '';
    }
    const rawAddress = address.rawAddress;
    const standardized = address.standardized;
    const stdAddress = address.stdAddress || '';
    const stdDeliveryLine = address.stdDeliveryLine || '';
    const stdCity = address.stdCity || '';
    const stdRegion = address.stdRegion || '';
    const stdPostalCode = address.stdPostalCode || '';
    const stdCountry = address.stdCountry || '';

    if (standardized) {
        return `${stdAddress}${showCountry ? `, ${stdCountry}` : ''}`;
    }
    if (standardized === false) {
        if (stdDeliveryLine && stdCity && stdRegion) {
            return `${stdDeliveryLine}, ${stdCity} ${stdRegion} ${stdPostalCode}${
                showCountry ? `, ${stdCountry}` : ''
            }`;
        }
        return rawAddress;
    }
    return '';
}

export function getAddressText(address) {
    if (address) {
        return `${address.stdDeliveryLine}, ${address.stdCity} ${
            address.stdRegion
        } ${address.stdPostalCode}${
            address.stdPostalCodePlus4 && address.standardized
                ? `-${address.stdPostalCodePlus4}`
                : ''
        }, ${address.stdCountry}`;
    }
    return 'N/A';
}

function _validateAdvancedAddress(address) {
    return ApiService.post(
        true,
        'Utilities/addresses/validateAdvancedAddress',
        {
            address,
        }
    ).then(response => response);
}

export function formatPostalCode(postalCode, country = 'US') {
    if (country === 'CA') {
        postalCode = postalCode
            .toUpperCase()
            .replace(/\W/g, '')
            .replace(/(...)/, '$1 ');
    }
    return postalCode;
}

export function validateAdvanceAddress(addressList) {
    const promiseAll = addressList.map(search => {
        const address = search.address;
        if (search.skip || address.standardized)
            return new Promise(resolve => {
                resolve({});
            });
        return new Promise((resolve, reject) => {
            _validateAdvancedAddress({
                city: address.stdCity,
                state: address.stdRegion,
                zip: formatPostalCode(
                    address.stdPostalCode,
                    address.stdCountry
                ),
                country: address.stdCountry,
            })
                .then(validateAddress => {
                    if (validateAddress.response.reason) {
                        throw new Error(validateAddress.response.reason);
                    }
                    resolve(validateAddress);
                })
                .catch(err => {
                    console.log(err);
                    if (search.type) {
                        err.message = `[${search.type}] ${err.message}`;
                    }
                    reject(err);
                });
        });
    });

    return Promise.all(promiseAll)
        .then(response => response) // Promise.all cannot be resolved, as one of the promises passed got rejected.
        .catch(error => {
            throw error;
        }); // Promise.all throws an error.
}

export function setAddressLatLong(address, _validateAddress, index) {
    if (_validateAddress.length >= index) {
        if (
            _validateAddress[index - 1].response &&
            _validateAddress[index - 1].response.zipcodes &&
            _validateAddress[index - 1].response.zipcodes.length > 0
        ) {
            return {
                ...address,
                latitude:
                    _validateAddress[index - 1]?.response.zipcodes[0]?.latitude,
                longitude:
                    _validateAddress[index - 1].response.zipcodes[0]?.longitude,
            };
        }
    }
    return address;
}
